import React from "react";
import { Link } from "@tanstack/react-router";
import { Lock } from "lucide-react";
import { useIsPro } from "../hooks/useIsPro";

/**
 * Wraps a Pro-only feature. Pro users see `children`; free users see a
 * blurred, non-interactive `preview` (or the children themselves) behind a
 * lock-and-upgrade overlay. Display-only — the matching endpoint must still
 * enforce the gate server-side (requirePro / 402 upgradeRequired).
 *
 * When the real content needs a Pro-only fetch, pass a static `preview` so
 * nothing is requested (and no 402 is triggered) for free users.
 */
export function ProGate({
  children,
  preview,
  label = "This is a Pro feature",
}: {
  children: React.ReactNode;
  preview?: React.ReactNode;
  label?: string;
}) {
  const { isPro } = useIsPro();
  if (isPro) return <>{children}</>;

  return (
    <div style={{ position: "relative" }}>
      <div
        aria-hidden="true"
        // inert blocks focus/interaction inside the blurred preview
        inert
        style={{ filter: "blur(6px)", pointerEvents: "none", userSelect: "none" }}
      >
        {preview ?? children}
      </div>
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 10,
          textAlign: "center",
          padding: 16,
        }}
      >
        <span
          style={{
            width: 34,
            height: 34,
            borderRadius: "var(--radius-pill)",
            background: "var(--accent-soft)",
            border: "1px solid var(--border-gold)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--gold-400)",
          }}
        >
          <Lock size={15} />
        </span>
        <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>{label}</span>
        <Link
          to="/plan"
          style={{
            padding: "7px 16px",
            background: "var(--accent)",
            color: "var(--text-on-gold)",
            borderRadius: "var(--radius-md)",
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          Upgrade to Pro
        </Link>
      </div>
    </div>
  );
}
